import { getDocument } from '@/lib/utils';

const TOKEN_TIMEOUT_MS = 3000;

type AwsWafWindow = Window & {
  AwsWafIntegration?: { getToken?: () => Promise<string> };
};

let sdkScriptLoad: Promise<boolean> | null = null;
// Lets later submits skip the wait while a timed-out one is still pending.
let timedOutTokenWait: Promise<boolean> | null = null;

export function loadAwsWafSdk(url: string): Promise<boolean> {
  const doc = getDocument();
  if (!doc) return Promise.resolve(false);

  sdkScriptLoad ??= new Promise((resolve) => {
    const script = doc.createElement('script');
    script.src = url;
    script.async = true;
    script.addEventListener('load', () => resolve(true), { once: true });
    script.addEventListener(
      'error',
      () => {
        // Allow a retry on the next call instead of skipping the token for the session.
        script.remove();
        sdkScriptLoad = null;
        resolve(false);
      },
      { once: true },
    );
    doc.head.append(script);
  });
  return sdkScriptLoad;
}

// Protected POSTs should carry the token; the cap keeps a blocked SDK from hanging submit.
export async function ensureAwsWafToken(url: string | null | undefined): Promise<void> {
  if (!url || timedOutTokenWait) return;

  const tokenReady = loadAwsWafSdk(url)
    .then((loaded) => {
      if (!loaded) return;
      const view = getDocument()?.defaultView as AwsWafWindow | null | undefined;
      return view?.AwsWafIntegration?.getToken?.();
    })
    .then(
      () => true,
      () => true,
    );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), TOKEN_TIMEOUT_MS);
  });

  try {
    if (await Promise.race([tokenReady, timeout])) return;

    timedOutTokenWait = tokenReady;
    void tokenReady.then(() => {
      if (timedOutTokenWait === tokenReady) timedOutTokenWait = null;
    });
  } finally {
    clearTimeout(timer);
  }
}
